import { AxiosError } from 'axios';
import format from 'date-fns/format';
import { eventChannel, EventChannel } from 'redux-saga';
import { cancelled, delay, put, call, select, take, takeLatest } from 'redux-saga/effects';
import { io, Socket } from 'socket.io-client';

import { types as eventTypes, eventNames } from 'services/events';
import { types as configurationTypes } from 'services/configuration';
import { selectors as ticketingSelectors } from 'services/ticketing';
import { selectors as hardwareSelectors, PrintData } from 'services/hardware';
import { getErrorMessage } from 'services/api/normalizer';

import { API, HardwareAPI, KioskAPI } from '../api/base';
import {
  types,
  Kiosk,
  KioskHardware,
  UpdateKioskAsyncRequest,
  GetKioskAsyncRequest,
  UpdateKioskHardwareAsyncRequest,
  CreateOrderTicketsAsyncRequest,
  ListenKioskUpdateServer,
  CreatePrintTicketsAsyncRequest,
  UpdateTransactionsAsyncRequest
} from './types';
import { selectors as kioskSelectors } from './selectors';
import { actions as kioskActions } from './reducer';
import { Configuration } from 'services/configuration';
import { types as hardwareTypes } from 'services/hardware';
import i18n from 'services/translation/i18n';

export function* getKioskAsync({ payload }: GetKioskAsyncRequest) {
  yield put({ type: types.GET_KIOSK_ASYNC.PENDING, payload: true });

  try {
    const kioskApi = new API('kiosks');
    const response: Kiosk = yield call(kioskApi.getOne, payload.kioskId);

    // Register each kiosk printer in the hardware store
    const kioskPrinterIds =
      response.hardware &&
      response.hardware.filter((hardware) => hardware.type === 'printer').map((hardware) => hardware.id);
    yield put({ type: hardwareTypes.REGISTER_KIOSK_PRINTERS, payload: kioskPrinterIds });

    yield put({ type: types.GET_KIOSK_ASYNC.SUCCESS, payload: response });
  } catch (err) {
    const message = getErrorMessage(err as AxiosError);

    yield put({ type: types.GET_KIOSK_ASYNC.ERROR, payload: message });
  } finally {
    yield put({ type: types.GET_KIOSK_ASYNC.PENDING, payload: false });
  }
}

export function* updateKioskAsync({ payload }: UpdateKioskAsyncRequest) {
  yield put({ type: types.UPDATE_KIOSK_ASYNC.PENDING, payload: true });

  try {
    const kiosk: Kiosk = yield select(kioskSelectors.selectKiosk);
    const kioskApi = new API('kiosks');
    const response: Kiosk = yield call(kioskApi.update, { id: kiosk.id, ...payload });
    yield put({ type: types.UPDATE_KIOSK_ASYNC.SUCCESS, payload: response });
  } catch (err) {
    const message = getErrorMessage(err as AxiosError);

    yield put({ type: types.UPDATE_KIOSK_ASYNC.ERROR, payload: message });
  } finally {
    yield put({ type: types.UPDATE_KIOSK_ASYNC.PENDING, payload: false });
  }
}

export function* rebootKioskAsync() {
  yield put({ type: types.REBOOT_KIOSK_ASYNC.PENDING, payload: true });

  try {
    const rebootAPI = new HardwareAPI('reboot');

    yield call(rebootAPI.create, {});
    yield put({
      type: eventTypes.LOG_EVENT_ASYNC.REQUEST,
      payload: {
        event: eventNames.KIOSK_REBOOTED,
        category: 'reboot',
        includeActor: true
      }
    });
    yield put({ type: types.REBOOT_KIOSK_ASYNC.SUCCESS });
  } catch (err) {
    const message = getErrorMessage(err as AxiosError);

    yield put({ type: types.REBOOT_KIOSK_ASYNC.ERROR, payload: message });
  } finally {
    yield put({ type: types.REBOOT_KIOSK_ASYNC.PENDING, payload: false });
  }
}

export function* rebootAdminKioskAsync() {
  yield put({ type: types.REBOOT_ADMIN_KIOSK_ASYNC.WAITING, payload: false });
  yield put({ type: types.REBOOT_ADMIN_KIOSK_ASYNC.PENDING, payload: true });
  yield delay(2000);
  try {
    const rebootAPI = new HardwareAPI('reboot');
    yield call(rebootAPI.create, {});
    yield put({ type: types.REBOOT_ADMIN_KIOSK_ASYNC.SUCCESS });
  } catch (err) {
    const message = getErrorMessage(err as AxiosError);

    yield put({ type: types.REBOOT_ADMIN_KIOSK_ASYNC.ERROR, payload: message });
  } finally {
    yield put({ type: types.REBOOT_ADMIN_KIOSK_ASYNC.PENDING, payload: false });
  }
}

export function* updateKioskHardwareAsync({ payload }: UpdateKioskHardwareAsyncRequest) {
  yield put({ type: types.UPDATE_KIOSK_HARDWARE_ASYNC.PENDING, payload: true });

  try {
    const { id: kioskHardwareId, ...updatePayload } = payload;

    const { id: kioskId }: Kiosk = yield select(kioskSelectors.selectKiosk);

    const kioskHardwareAPI = new API(`kiosks/${kioskId}/hardware`);
    const response: KioskHardware = yield call(kioskHardwareAPI.update, { id: kioskHardwareId, ...updatePayload });

    yield put({ type: types.UPDATE_KIOSK_HARDWARE_ASYNC.SUCCESS, payload: response });
  } catch (err) {
    const message = getErrorMessage(err as AxiosError);

    yield put({ type: types.UPDATE_KIOSK_HARDWARE_ASYNC.ERROR, payload: message });
  } finally {
    yield put({ type: types.UPDATE_KIOSK_HARDWARE_ASYNC.PENDING, payload: false });
  }
}

export function* updateTransactionsAsync({ payload }: UpdateTransactionsAsyncRequest) {
  try {
    const { id: kioskId }: Kiosk = yield select(kioskSelectors.selectKiosk);

    const api = new API(`kiosks/${kioskId}/transactions/updateTransactions`);
    yield call(api.create, payload);
  } catch (err) {
    // no-op
  }
}

interface PrintResponse {
  ticketInfo: {
    printString: string;
    ticketNumber: string;
    transactionId: string;
    kioskHardwareId?: string;
  }[];
}

export function* createTicketsToPrintAsync({ payload }: CreatePrintTicketsAsyncRequest) {
  yield put({ type: types.CREATE_PRINT_TICKETS_ASYNC.PENDING, payload: true });

  try {
    const session: string | null = yield select(ticketingSelectors.selectSession);

    const ticketsToPrintApi = new API(`ticketing/tickets/print?session=${session}`);
    const response: PrintResponse = yield call(ticketsToPrintApi.create, payload);

    const tickets = response.ticketInfo.map((ticket) => ({
      message: ticket.printString,
      ticketNumber: ticket.ticketNumber,
      transactionId: ticket.transactionId,
      kioskHardwareId: ticket.kioskHardwareId
    }));

    if (!tickets.length) {
      throw new Error('There are no tickets to print');
    }

    yield put({ type: types.CREATE_PRINT_TICKETS_ASYNC.SUCCESS, payload: tickets });
    yield put;
  } catch (err) {
    const message = getErrorMessage(err as AxiosError);

    yield put({ type: types.CREATE_PRINT_TICKETS_ASYNC.ERROR, payload: message });
  } finally {
    yield put({ type: types.CREATE_PRINT_TICKETS_ASYNC.PENDING, payload: false });
  }
}

export function* createOrderTicketsToPrintAsync({ payload }: CreateOrderTicketsAsyncRequest) {
  yield put({ type: types.CREATE_ORDER_TICKETS_ASYNC.PENDING, payload: true });

  try {
    const session: string | null = yield select(ticketingSelectors.selectSession);
    const ticketsToPrintApi = new API(`ticketing/order/print?session=${session}`);
    const response: PrintResponse = yield call(ticketsToPrintApi.create, payload);

    const tickets = response.ticketInfo.map((ticket) => ({
      message: ticket.printString,
      ticketNumber: ticket.ticketNumber,
      transactionId: ticket.transactionId,
      kioskHardwareId: ticket.kioskHardwareId
    }));

    yield put({ type: types.CREATE_ORDER_TICKETS_ASYNC.SUCCESS, payload: tickets });
    yield put;
  } catch (err) {
    const message = getErrorMessage(err as AxiosError) || i18n.t('saleError.printErrorHeader');

    yield put({ type: types.CREATE_ORDER_TICKETS_ASYNC.ERROR, payload: message });
  } finally {
    yield put({ type: types.CREATE_ORDER_TICKETS_ASYNC.PENDING, payload: false });
  }
}

export function* checkSchedule() {
  while (true) {
    const kiosk: Kiosk = yield select(kioskSelectors.selectKiosk);
    const schedule = kiosk.schedule || [];

    const now = new Date(Date.now()); // We have to use Date.now for mock tests

    /* Don't check the schedule if
     *
     * 1. The kiosk is unscheduled until some time
     * 2. There are any hardware errors
     * 3. The ticket stock is below the threshold
     */
    // If the kiosk is unscheduled mode, don't check the schedule until we care
    const unscheduledUntil = kiosk.unscheduledUntil && new Date(kiosk.unscheduledUntil);
    const anyHardwareErrors: boolean = yield select(hardwareSelectors.selectAnyHardwareErrors);
    const lowStockThresholdReached: boolean = yield select(kioskSelectors.selectLowStockThresholdReached);
    if ((unscheduledUntil && now < unscheduledUntil) || anyHardwareErrors || lowStockThresholdReached) {
      yield delay(3000);

      continue;
    }

    const today = parseInt(format(now, 'e'), 10) - 1;
    const timeRightNow = parseInt(format(now, 'Hmm'), 10);

    const scheduleDay = schedule.find((d) => d.day === today);
    let active = scheduleDay && scheduleDay.startTime <= timeRightNow && timeRightNow < scheduleDay.endTime;
    active = !active ? scheduleDay && scheduleDay.startTime === scheduleDay.endTime : active;
    const status = active ? 'Active' : 'Inactive';

    // Update kiosk if the status is different than what is listed
    if (status !== kiosk.status) {
      yield put({ type: types.UPDATE_KIOSK_ASYNC.REQUEST, payload: { status } });
      yield put({ type: types.SET_STATUS_ACTOR, payload: 'schedule' });
    }

    yield put({ type: types.CHECK_SCHEDULE.SUCCESS, payload: status });
    yield delay(3000);
  }
}

export function* listenSocketServer({ payload }: ListenKioskUpdateServer): any {
  const { kioskId, configurationId, clientId } = payload;

  let openedSocket = false;
  let retryDelay = 500;
  let timeoutId: ReturnType<typeof setTimeout>;

  const reconnect = (delayTime: number) => {
    if (openedSocket === false) {
      timeoutId = setTimeout(async () => {
        await connect();
      }, delayTime);
    }
  };

  const createSocketChannel = (socket: Socket) =>
    eventChannel((emit: any) => {
      const updateHandler = (data: Partial<Kiosk>) => emit({ type: 'update', data });
      const updateConfigHandler = (data: Configuration) => emit({ type: 'update_configuration', data });
      const rebootHandler = () => emit({ type: 'reboot' });

      socket.on(`kiosk_update:${kioskId}`, updateHandler);
      socket.on(`kiosk_reboot:${kioskId}`, rebootHandler);
      socket.on(`configuration_update:${configurationId}`, updateConfigHandler);

      return () => {
        socket.off(`kiosk_update:${kioskId}`, updateHandler);
        socket.off(`kiosk_reboot:${kioskId}`, rebootHandler);
        socket.off(`configuration_update:${configurationId}`, updateConfigHandler);
      };
    });

  const connect = () => {
    const socket = io(`${process.env.REACT_APP_API_BASE}`, { autoConnect: false, transports: ['websocket'] });
    socket.auth = { kioskId, clientId };
    socket.connect();

    return new Promise((resolve) => {
      socket.on('connect_error', () => {
        socket.disconnect();
        openedSocket = false;
        retryDelay = retryDelay >= 16000 ? retryDelay : retryDelay + retryDelay;
        resolve(socket);
        reconnect(retryDelay);
      });

      socket.on('connect_timeout', () => {
        openedSocket = false;
        socket.disconnect();
      });

      socket.on('disconnect', () => {
        openedSocket = false;
      });

      socket.on('connect', () => {
        resolve(socket);
        openedSocket = true;
        retryDelay = 500;
        clearTimeout(timeoutId);
      });
    });
  };

  let socket: Socket | undefined;
  try {
    // connect to the server
    socket = yield call(connect);

    // then create a socket channel
    const socketChannel: EventChannel<any> = yield call(createSocketChannel, socket as Socket);

    // then put the new data into the reducer
    while (true) {
      const { type, data } = yield take(socketChannel);
      switch (type) {
        case 'update':
          const { data: updatedKiosk } = data;
          // transaction and will call process will continue, if the kiosk goes offline
          if (
            updatedKiosk.status === 'Inactive' &&
            window.location.pathname !== '/ticketing/attract' &&
            window.location.pathname !== '/ticketing/info'
          ) {
            yield put({ type: types.UPDATE_KIOSK_ASYNC.WAITING, payload: true });
          } else {
            const kiosk: Kiosk = yield select(kioskSelectors.selectKiosk);
            if (kiosk.status !== updatedKiosk.status) {
              yield put({ type: types.SET_STATUS_ACTOR, payload: 'kcc' });
            }
            yield put({ type: types.UPDATE_KIOSK_ASYNC.WAITING, payload: false });
            yield put({ type: types.UPDATE_KIOSK_ASYNC.SUCCESS, payload: updatedKiosk });
          }
          break;
        case 'reboot':
          // transaction and will call process will continue, if the kiosk has been rebooted
          if (
            (window.location.pathname.includes('/ticketing') || window.location.pathname.includes('/willcall')) &&
            window.location.pathname !== '/ticketing/attract' &&
            window.location.pathname !== '/ticketing/info' &&
            window.location.pathname !== '/ticketing/offline'
          ) {
            yield put({ type: types.REBOOT_ADMIN_KIOSK_ASYNC.WAITING, payload: true });
          } else {
            yield put({ type: types.REBOOT_ADMIN_KIOSK_ASYNC.WAITING, payload: false });
            yield put({ type: types.REBOOT_ADMIN_KIOSK_ASYNC.REQUEST });
          }
          break;
        case 'update_configuration':
          const { data: updatedConfig } = data;
          yield put({ type: configurationTypes.GET_CONFIG_ASYNC.SUCCESS, payload: updatedConfig });
          break;
        default:
      }
    }
  } finally {
    if (yield cancelled() && socket) {
      (socket as unknown as Socket).disconnect();
    }
  }
}

export function* decrementStockCountsAsync() {
  const { printJobs }: PrintData = yield select(hardwareSelectors.selectPrintData);
  const kioskPrinters: KioskHardware[] = yield select(kioskSelectors.selectPrinters);

  const jobsThatPrinted = printJobs.filter((job) => job.printed);

  const jobCountPerPrinter: Record<string, number> = {};
  for (const printJob of jobsThatPrinted) {
    const kioskHardwareId = printJob.kioskHardwareId || kioskPrinters[0].id;

    if (jobCountPerPrinter[kioskHardwareId]) {
      jobCountPerPrinter[kioskHardwareId]++;
    } else {
      jobCountPerPrinter[kioskHardwareId] = 1;
    }
  }

  for (const [kioskHardwareId, count] of Object.entries(jobCountPerPrinter)) {
    const kioskPrinter = kioskPrinters.find((p) => p.id === kioskHardwareId) as KioskHardware;
    const currentStock = kioskPrinter.values.currentStock || 0;

    yield call(updateKioskHardwareAsync, {
      type: 'UPDATE_KIOSK_HARDWARE_ASYNC.REQUEST',
      payload: {
        id: kioskHardwareId,
        values: {
          currentStock: currentStock - count
        }
      }
    });
  }
}

export function* checkLowStockThresholds() {
  const lowStockThresholdReached: boolean = yield select(kioskSelectors.selectLowStockThresholdReached);

  if (lowStockThresholdReached) {
    yield put(kioskActions.updateKiosk({ status: 'Inactive' }));
    yield put(kioskActions.setStatusActor('lowStock'));
  }
}

export function* checkKioskVersion() {
  try {
    yield put({ type: types.CHECK_KIOSK_VERSION_ASYNC.PENDING, payload: true });
    const kioskVersionApi = new KioskAPI(`/meta.json?${new Date().getTime()}`);
    const response: { version: string } = yield call(kioskVersionApi.getAll);
    if (response && response.version) {
      yield put({ type: types.CHECK_KIOSK_VERSION_ASYNC.SUCCESS, payload: response.version });
    }
  } catch (err) {
    // Kiosk version check Failure
    const message = getErrorMessage(err as AxiosError);
    yield put({ type: types.CHECK_KIOSK_VERSION_ASYNC.ERROR, payload: message });
  } finally {
    yield put({ type: types.CHECK_KIOSK_VERSION_ASYNC.PENDING, payload: false });
  }
}

export const sagas = [
  takeLatest(types.GET_KIOSK_ASYNC.REQUEST, getKioskAsync),
  takeLatest(types.UPDATE_KIOSK_ASYNC.REQUEST, updateKioskAsync),
  takeLatest(types.REBOOT_KIOSK_ASYNC.REQUEST, rebootKioskAsync),
  takeLatest(types.REBOOT_ADMIN_KIOSK_ASYNC.REQUEST, rebootAdminKioskAsync),
  takeLatest(types.UPDATE_KIOSK_HARDWARE_ASYNC.REQUEST, updateKioskHardwareAsync),
  takeLatest(types.CREATE_PRINT_TICKETS_ASYNC.REQUEST, createTicketsToPrintAsync),
  takeLatest(types.CREATE_ORDER_TICKETS_ASYNC.REQUEST, createOrderTicketsToPrintAsync),
  takeLatest(types.CHECK_SCHEDULE.REQUEST, checkSchedule),
  takeLatest(types.LISTEN_SOCKET_SERVER, listenSocketServer),
  takeLatest(types.UPDATE_TRANSACTIONS_ASYNC.REQUEST, updateTransactionsAsync),
  takeLatest(types.DECREMENT_STOCK_COUNTS_ASYNC.REQUEST, decrementStockCountsAsync),
  takeLatest(types.CHECK_LOW_STOCK_THRESHOLDS, checkLowStockThresholds),
  takeLatest(types.RELOAD_APPLICATION, () => window.location.reload()),
  takeLatest(types.CHECK_KIOSK_VERSION_ASYNC.REQUEST, checkKioskVersion)
];
